import { Binary } from "./binary.js";
import { valveLump, PakFile } from "./compression.js";
import { entities } from "./vmt.js";
import { logger } from "./log.js";
import { worldLights, vertexLighting } from "./lighting-data.js";
import { lightStylePatterns, lightStyleValue } from "./lightstyles.js";
const log = logger("BSP");
function bspLumps(buffer, progress = () => {}) {
    const file = new Binary(buffer);
    if (file.text(0, 4) !== "VBSP" || ![19, 20].includes(file.i32(4)))
        throw new Error("Expected TF2 BSP version 19 or 20");
    file.check(0, 1036);
    const directory = Array.from({ length: 64 }, (_, i) => ({
        offset: file.i32(8 + i * 16),
        length: file.i32(12 + i * 16),
        version: file.i32(16 + i * 16),
    }));
    const cache = new Map();
    const lump = (id) => {
        if (!cache.has(id)) {
            const d = directory[id];
            log.debug("lump", { id, offset: d.offset, length: d.length, version: d.version });
            progress(`Parsing BSP · lump ${id}`);
            cache.set(
                id,
                new Binary(valveLump(file.slice(d.offset, d.length))),
            );
        }
        return cache.get(id);
    };
    return { lump, file, directory };
}
export function parseBspPak(buffer, diagnostic = () => {}) {
    return new PakFile(bspLumps(buffer).lump(40).bytes, diagnostic);
}
export function parseBsp(buffer, diagnostic = () => {}, progress = () => {}) {
    const started = performance.now();
    const { lump, file, directory } = bspLumps(buffer, progress);
    const pak = new PakFile(lump(40).bytes, diagnostic),
        ent = entities(lump(0).text(0, lump(0).bytes.length));
    const patterns = lightStylePatterns(ent);
    const vertices = lump(3),
        edges = lump(12),
        surfedges = lump(13),
        texinfo = lump(6),
        texdata = lump(2),
        strings = lump(43),
        table = lump(44);
    const hdr = directory[58].length > 0 && directory[53].length > 0;
    const faces = lump(hdr ? 58 : 7),
        light = lump(hdr ? 53 : 8);
    log.info("face set", { hdr, faces: faces.bytes.length / 56, lightBytes: light.bytes.length });
    if (faces.bytes.length % 56) throw new Error("Unsupported BSP face layout");
    const materialNames = Array.from(
        { length: Math.floor(texdata.bytes.length / 32) },
        (_, i) =>
            strings
                .string(table.i32(texdata.i32(i * 32 + 12) * 4))
                .toLowerCase(),
    );
    const disp = lump(26),
        dispverts = lump(33),
        disptris = lump(48),
        model = lump(14);
    const models = Array.from(
        { length: Math.floor(model.bytes.length / 48) },
        (_, i) => ({
            mins: model.vec(i * 48),
            maxs: model.vec(i * 48 + 12),
            origin: model.vec(i * 48 + 24),
            headnode: model.i32(i * 48 + 36),
            first: model.i32(i * 48 + 40),
            count: model.i32(i * 48 + 44),
        }),
    );
    const faceModels = new Int32Array(faces.bytes.length / 56);
    models.forEach((m, i) => {
        for (let j = m.first; j < m.first + m.count; j++) {
            if (j < 0 || j >= faceModels.length)
                throw new Error("Invalid model face range");
            faceModels[j] = i;
        }
    });
    const geometry = [];
    let vertexBudget = 0,
        lightBudget = 0;
    if (faces.bytes.length / 56 > 100000)
        throw new Error("Map exceeds face budget");
    for (let face = 0; face < faces.bytes.length / 56; face++) {
        if (face % 1000 === 0)
            progress(
                `Building geometry · ${face} / ${faces.bytes.length / 56} faces`,
            );
        const f = face * 56,
            ti = faces.i16(f + 10);
        if (ti < 0) continue;
        const t = ti * 72,
            flags = texinfo.i32(t + 64),
            td = texinfo.i32(t + 68);
        if (flags & (0x80 | 0x200 | 0x10000) || flags & 0x4) continue; // nodraw, skip, hint, sky
        const texture = materialNames[td];
        if (
            !texture ||
            (texture.startsWith("tools/") &&
                !["tools/toolsblack", "tools/toolswhite"].includes(texture))
        )
            continue;
        const edgeCount = faces.i16(f + 8),
            first = faces.i32(f + 4),
            polygon = [];
        for (let n = 0; n < edgeCount; n++) {
            const e = surfedges.i32((first + n) * 4);
            polygon.push(
                vertices.vec(edges.u16(Math.abs(e) * 4 + (e < 0 ? 2 : 0)) * 12),
            );
        }
        if (polygon.length < 3) continue;
        let basePoints = polygon;
        let points = polygon,
            alpha = points.map(() => 0),
            indices = [];
        const di = faces.i16(f + 12);
        if (di >= 0 && polygon.length === 4) {
            const d = di * 176,
                start = disp.vec(d),
                power = disp.i32(d + 20),
                begin = disp.i32(d + 12),
                triStart = disp.i32(d + 16);
            if (power < 2 || power > 4)
                throw new Error("Unsupported displacement power");
            const side = (1 << power) + 1;
            const closest = polygon
                .map((p) =>
                    p.reduce((sum, x, j) => sum + (x - start[j]) ** 2, 0),
                )
                .reduce((best, x, i, arr) => (x < arr[best] ? i : best), 0);
            const corners = Array.from(
                { length: 4 },
                (_, i) => polygon[(closest + i) % 4],
            );
            points = [];
            basePoints = [];
            alpha = [];
            for (let y = 0; y < side; y++)
                for (let x = 0; x < side; x++) {
                    const u = x / (side - 1),
                        v = y / (side - 1),
                        dv = (begin + y * side + x) * 20,
                        normal = dispverts.vec(dv),
                        distance = dispverts.f32(dv + 12);
                    const base = corners[0].map(
                        (_, k) =>
                            (corners[0][k] * (1 - u) + corners[3][k] * u) *
                                (1 - v) +
                            (corners[1][k] * (1 - u) + corners[2][k] * u) * v,
                    );
                    basePoints.push(base);
                    points.push(
                        base.map((value, k) => value + normal[k] * distance),
                    );
                    alpha.push(dispverts.f32(dv + 16) / 255);
                }
            for (let y = 0; y < side - 1; y++)
                for (let x = 0; x < side - 1; x++) {
                    const a = y * side + x,
                        b = a + 1,
                        c = a + side,
                        d2 = c + 1;
                    const tris =
                        (x + y) % 2
                            ? [
                                  [a, c, b],
                                  [b, c, d2],
                              ]
                            : [
                                  [a, c, d2],
                                  [a, d2, b],
                              ];
                    tris.forEach((tri, i) => {
                        const index =
                            (triStart + (y * (side - 1) + x) * 2 + i) * 2;
                        // Walkable/buildable tags do not determine whether a
                        // triangle is drawn. Only DISPTRI_TAG_REMOVE cuts a hole.
                        if (
                            !disptris.bytes.length ||
                            !(disptris.u16(index) & 0x20)
                        )
                            indices.push(tri[0], tri[2], tri[1]);
                    });
                }
        } else
            for (let i = 1; i < polygon.length - 1; i++)
                // Source stores clockwise faces; WebGL front faces are CCW.
                indices.push(0, i + 1, i);
        vertexBudget += points.length;
        if (vertexBudget > 4000000)
            throw new Error("Map exceeds geometry budget");
        const width = texdata.i32(td * 32 + 16),
            height = texdata.i32(td * 32 + 20);
        const lmW = faces.i32(f + 36) + 1,
            lmH = faces.i32(f + 40) + 1,
            lightOffset = faces.i32(f + 20);
        let lighting = null;
        if (lightOffset >= 0 && lmW > 0 && lmH > 0 && lmW * lmH <= 4_194_304) {
            lightBudget += lmW * lmH;
            if (lightBudget > 16777216)
                throw new Error("Map exceeds lightmap budget");
            // Surface light is the sum of its baked style layers. Named lights
            // use the map's initial on/off pattern, including styles >= 32.
            const styles = [];
            for (let slot = 0; slot < 4; slot++) {
                const style = faces.u8(f + 16 + slot);
                if (style === 255 || styles.includes(style)) break;
                styles.push(style);
            }
            const stride = lmW * lmH * 4 * (flags & 0x800 ? 4 : 1); // base plus three bump maps per style
            light.check(lightOffset, styles.length ? (styles.length - 1) * stride + lmW * lmH * 4 : lmW * lmH * 4);
            lighting = new Float32Array(lmW * lmH * 4);
            for (let slot = 0; slot < styles.length; slot++) {
                const amount = lightStyleValue(styles[slot], 0, patterns);
                for (let i = 0; i < lmW * lmH; i++) {
                    const offset = lightOffset + slot * stride + i * 4,
                        exponent = 2 ** light.i8(offset + 3);
                    for (let j = 0; j < 3; j++)
                        lighting[i * 4 + j] += light.u8(offset + j) * exponent * amount / 255;
                }
            }
            for (let i = 0; i < lmW * lmH; i++) lighting[i * 4 + 3] = 1;
        }
        const uv = [],
            uv2 = [];
        for (const p of basePoints)
            for (let axis = 0; axis < 2; axis++) {
                const base = t + axis * 16;
                uv.push(
                    p.reduce(
                        (s, x, j) => s + x * texinfo.f32(base + j * 4),
                        texinfo.f32(base + 12),
                    ) / Math.max(1, axis ? height : width),
                );
                const lm = t + 32 + axis * 16;
                uv2.push(
                    (p.reduce(
                        (s, x, j) => s + x * texinfo.f32(lm + j * 4),
                        texinfo.f32(lm + 12),
                    ) -
                        faces.i32(f + 28 + axis * 4) +
                        0.5) /
                        (axis ? lmH : lmW),
                );
            }
        geometry.push({
            face,
            model: faceModels[face],
            displacement: di >= 0,
            water: !!(flags & 0x8),
            surfaceFlags: flags,
            texture,
            positions: new Float32Array(points.flat()),
            uv: new Float32Array(uv),
            uv2: new Float32Array(uv2),
            alpha: new Float32Array(alpha),
            indices: new Uint32Array(indices),
            lighting,
            lmW,
            lmH,
        });
    }
    const nodes = lump(5),
        planes = lump(1),
        leaves = lump(10),
        leafFaces = lump(16),
        vis = lump(4);
    const leafStride =
        directory[10].version === 0 ? 56 : directory[10].version === 1 ? 32 : 0;
    const visibility = {
        nodes: nodes.bytes.slice(),
        planes: planes.bytes.slice(),
        leaves: leaves.bytes.slice(),
        leafFaces: leafFaces.bytes.slice(),
        vis: vis.bytes.slice(),
        leafBrushes: lump(17).bytes.slice(), brushes: lump(18).bytes.slice(),
        brushSides: lump(19).bytes.slice(), texinfo: texinfo.bytes.slice(),
        texdata: texdata.bytes.slice(), materialNames,
        ambientIndex: lump(hdr ? 51 : 52).bytes.slice(),
        ambient: lump(hdr ? 55 : 56).bytes.slice(),
        leafStride, rootNode: models[0]?.headnode ?? 0,
    };
    const lighting = { hdr, worldLights: worldLights(lump(hdr && directory[54].length ? 54 : 15).bytes) };
    // VRAD can emit uniform 0.1 placeholder luxels on maps with no baked
    // light sources (e.g. jump_air_beta2). Keep runtime lights, but do not
    // multiply every surface by those near-black placeholder lightmaps.
    lighting.placeholder = !lighting.worldLights.length && !ent.some(e =>
        ["light", "light_spot", "light_environment"].includes(e.classname)) &&
        geometry.some(face => face.lighting) && geometry.every(face => !face.lighting ||
            face.lighting.every((value, index) => index % 4 === 3 || value <= 0.1 / 255 + 1e-7));
    if (lighting.placeholder) for (const face of geometry) face.lighting = null;
    const props = [];
    try {
        const game = lump(35);
        if (game.bytes.length)
            for (let i = 0; i < game.i32(0); i++) {
                const o = 4 + i * 16;
                if (game.u32(o) !== 0x73707270) continue;
                const version = game.u16(o + 6),
                    strides = {
                        4: [56],
                        5: [60],
                        6: [64],
                        7: [68, 72],
                        8: [68],
                        9: [72],
                        10: [72, 76],
                        11: [80],
                    };
                let stride = strides[version];
                if (!stride) {
                    diagnostic(`Unsupported static prop version ${version}`);
                    continue;
                }
                const absolute = game.i32(o + 8),
                    length = game.i32(o + 12);
                const bytes =
                    directory[35].length &&
                    file.text(
                        directory[35].offset,
                        Math.min(4, directory[35].length),
                    ) === "LZMA"
                        ? game.slice(absolute - directory[35].offset, length)
                        : file.slice(absolute, length);
                const r = new Binary(valveLump(bytes));
                let offset = 4;
                const names = [];
                for (let j = 0; j < r.i32(0); j++) {
                    names.push(r.string(offset, 128));
                    offset += 128;
                }
                const leafCount = r.i32(offset);
                const propLeaves = offset + 4;
                offset += 4 + leafCount * 2;
                const count = r.i32(offset);
                offset += 4;
                stride = stride.find(
                    (size) => count * size === r.bytes.length - offset,
                );
                if (!stride)
                    throw new Error("Unsupported static-prop record size");
                r.check(offset, count * stride);
                for (let j = 0; j < count; j++, offset += stride) {
                    // TF2 v10 moved flags to a 32-bit field after the DX levels.
                    // Byte 31 is padding and may contain arbitrary compiler data.
                    const flags = version === 10 && stride === 72 ? r.u32(offset + 64) : r.u8(offset + 31),
                        baked = pak.get(`sp${hdr ? "_hdr" : ""}_${j}.vhv`);
                    let bakedLighting = null;
                    if (baked && !(flags & 0x40)) {
                        try { bakedLighting = vertexLighting(baked); }
                        catch (error) { log.warn("invalid baked prop lighting; using probes", { prop: j, message: error.message }); }
                    }
                    props.push({
                        path: names[r.u16(offset + 24)],
                        origin: r.vec(offset),
                        angles: r.vec(offset + 12),
                        skin: r.i32(offset + 32),
                        scale: version >= 11 ? r.f32(offset + 76) : 1,
                        flags, lightingOrigin: flags & 2 ? r.vec(offset + 44) : null,
                        leaves: Array.from({ length: r.u16(offset + 28) }, (_, i) => {
                            const index = r.u16(offset + 26) + i;
                            if (index >= leafCount) throw new Error("Invalid static prop leaf range");
                            return r.u16(propLeaves + index * 2);
                        }),
                        bakedLighting, static: true,
                    });
                }
            }
    } catch (e) {
        log.error("static props failed", e);
        diagnostic(`Static props: ${e.message}`);
    }
    for (const e of ent)
        if (
            ["prop_dynamic", "prop_dynamic_override", "prop_physics", "prop_physics_multiplayer"].includes(
                e.classname,
            ) &&
            e.model &&
            !e.model.startsWith("*")
        )
            props.push({
                path: e.model,
                origin: numbers(e.origin),
                angles: numbers(e.angles),
                skin: Number(e.skin) || 0,
                scale: Number(e.modelscale) || 1,
                lightingOrigin: e.lightingorigin && ent.find(target => target.targetname === e.lightingorigin)?.origin
                    ? numbers(ent.find(target => target.targetname === e.lightingorigin).origin) : null,
            });
    log.info("parse complete", { faces: geometry.length, vertices: vertexBudget, lightLuxels: lightBudget,
        models: models.length, entities: ent.length, props: props.length, durationMs: performance.now() - started });
    return { pak, geometry, entities: ent, models, visibility, props, lighting };
}
export function numbers(value = "0 0 0") {
    const v = value.split(/\s+/).map(Number);
    return v.length === 3 && v.every(Number.isFinite) ? v : [0, 0, 0];
}
